// @ts-nocheck
// CAMINHO: src/components/cases/MovementsTab.tsx
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ExternalLink, PlusCircle, Edit, Trash2, GanttChartSquare } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Case } from '@/types/case';
import { Movimentacao, ProcessData } from '@/types/datajud';
import { AddMovementModal } from './AddMovementModal';
import { EditMovementModal } from './EditMovementModal'; // Será criado em breve
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

interface MovementsTabProps {
  movements: Movimentacao[];
  caseDetails: Case;
  onDataUpdate: () => void; // Callback para o componente pai atualizar os dados
}

const getPublicUrlForProcess = (case_: Case): string => {
    const numeroLimpo = (case_.case_number_cnj || case_.case_number || '').replace(/\D/g, '');
    if (!numeroLimpo) return '#';
    const tribunal = case_.court || case_.datajud_json?.tribunal || '';
    return `https://www.google.com/search?q=${numeroLimpo}+${tribunal}`;
}

export const MovementsTab = ({ movements, caseDetails, onDataUpdate }: MovementsTabProps) => {
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [currentMovement, setCurrentMovement] = useState<Movimentacao | null>(null);
    const [currentMovementIndex, setCurrentMovementIndex] = useState<number | null>(null);
    const { toast } = useToast();

    const handleAddMovementSuccess = async (updatedDatajudJson: ProcessData) => {
        try {
            const { error } = await supabase
                .from('cases')
                .update({ datajud_json: updatedDatajudJson })
                .eq('id', caseDetails.id);

            if (error) throw error;

            // Registrar na timeline: novo andamento
            try {
                const updatedMovs = (updatedDatajudJson?.movimentacoes || []);
                const newMovement = updatedMovs[updatedMovs.length - 1];
                const user = (await supabase.auth.getUser()).data.user;
                await (supabase as any)
                    .from('case_timeline_entries')
                    .insert({
                        case_id: caseDetails.id,
                        type: 'movement',
                        title: newMovement?.nome || 'Andamento adicionado',
                        description: newMovement?.complemento || null,
                        entry_date: newMovement?.dataHora || new Date().toISOString(),
                        details: { action: 'added', movement: newMovement, user_id: user?.id || null }
                    });
            } catch (timelineErr) {
                console.warn('Falha ao registrar timeline (add movement):', timelineErr);
            }

            toast({ title: "Sucesso!", description: "Andamento adicionado e salvo." });
            onDataUpdate(); // Notifica o pai para re-buscar os dados
        } catch (err: any) {
            toast({ title: "Erro ao salvar andamento", description: err.message, variant: "destructive" });
        }
    };

    const handleEditMovementSuccess = async (updatedMovement: Movimentacao) => {
        if (currentMovementIndex === null) return;

        const allMovements = [...(caseDetails.datajud_json?.movimentacoes || [])];
        allMovements[currentMovementIndex] = updatedMovement;

        const updatedDatajudJson = { ...caseDetails.datajud_json, movimentacoes: allMovements } as ProcessData;

        try {
            const { error } = await supabase
                .from('cases')
                .update({ datajud_json: updatedDatajudJson })
                .eq('id', caseDetails.id);

            if (error) throw error;

            // Registrar na timeline: andamento editado
            try {
                const user = (await supabase.auth.getUser()).data.user;
                await (supabase as any)
                    .from('case_timeline_entries')
                    .insert({
                        case_id: caseDetails.id,
                        type: 'movement',
                        title: updatedMovement?.nome ? `Andamento editado: ${updatedMovement.nome}` : 'Andamento editado',
                        description: updatedMovement?.complemento || null,
                        entry_date: updatedMovement?.dataHora || new Date().toISOString(),
                        details: { action: 'edited', movement_index: currentMovementIndex, movement: updatedMovement, user_id: user?.id || null }
                    });
            } catch (timelineErr) {
                console.warn('Falha ao registrar timeline (edit movement):', timelineErr);
            }

            toast({ title: "Sucesso!", description: "Andamento editado e salvo." });
            onDataUpdate(); // Notifica o pai para re-buscar os dados
        } catch (err: any) {
            toast({ title: "Erro ao salvar andamento", description: err.message, variant: "destructive" });
        }
    };

    const handleDeleteMovement = async (indexToDelete: number) => {
        if (!window.confirm("Tem certeza que deseja excluir este andamento?")) return;

        const allMovements = (caseDetails.datajud_json?.movimentacoes || []).filter((_, index) => index !== indexToDelete);
        const updatedDatajudJson = { ...caseDetails.datajud_json, movimentacoes: allMovements } as ProcessData;

        try {
            const { error } = await supabase
                .from('cases')
                .update({ datajud_json: updatedDatajudJson })
                .eq('id', caseDetails.id);

            if (error) throw error;
            toast({ title: "Sucesso!", description: "Andamento excluído." });
            // Registrar na timeline: andamento excluído
            try {
                const deletedMovement = caseDetails.datajud_json?.movimentacoes?.[indexToDelete];
                const user = (await supabase.auth.getUser()).data.user;
                await (supabase as any)
                    .from('case_timeline_entries')
                    .insert({
                        case_id: caseDetails.id,
                        type: 'movement',
                        title: deletedMovement?.nome ? `Andamento excluído: ${deletedMovement.nome}` : 'Andamento excluído',
                        description: deletedMovement?.complemento || null,
                        entry_date: deletedMovement?.dataHora || new Date().toISOString(),
                        details: { action: 'deleted', movement_index: indexToDelete, movement: deletedMovement, user_id: user?.id || null }
                    });
            } catch (timelineErr) {
                console.warn('Falha ao registrar timeline (delete movement):', timelineErr);
            }
            onDataUpdate();
        } catch (err: any) {
            toast({ title: "Erro ao excluir andamento", description: err.message, variant: "destructive" });
        }
    };

    return (
        <Card>
            <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Andamentos Processuais</CardTitle>
                <Button size="sm" onClick={() => setIsAddModalOpen(true)}>
                    <PlusCircle className="w-4 h-4 mr-2" />Adicionar Andamento
                </Button>
            </CardHeader>
            <CardContent className="space-y-4">
                {(movements || []).length > 0 ? (
                    (movements || []).map((mov, index) => (
                        <div key={index} className="text-sm border-b pb-3 last:border-b-0">
                            <div className="flex justify-between items-start gap-2">
                                <p className="font-semibold flex-1">{mov.nome}</p>
                                <div className="flex gap-1">
                                    <Button 
                                        variant="ghost" 
                                        size="icon" 
                                        onClick={() => {
                                            setCurrentMovement(mov);
                                            setCurrentMovementIndex(index);
                                            setIsEditModalOpen(true);
                                        }}
                                    >
                                        <Edit className="w-4 h-4" />
                                    </Button>
                                    <Button 
                                        variant="ghost" 
                                        size="icon" 
                                        onClick={() => handleDeleteMovement(index)}
                                    >
                                        <Trash2 className="w-4 h-4 text-destructive" />
                                    </Button>
                                    <a href={getPublicUrlForProcess(caseDetails)} target="_blank" rel="noopener noreferrer">
                                        <Button variant="ghost" size="sm" className="h-7 text-xs px-2">
                                            <ExternalLink className="w-3 h-3 mr-1" />Ver no Tribunal
                                        </Button>
                                    </a>
                                </div>
                            </div>
                            <p className="text-xs text-muted-foreground">
                                {mov.dataHora ? format(parseISO(mov.dataHora), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR }) : 'Data não informada'}
                            </p>
                            {mov.complemento && <p className="text-sm mt-2 bg-slate-50 p-3 rounded border whitespace-pre-wrap">{mov.complemento}</p>}
                        </div>
                    ))
                ) : (
                    <div className="text-center text-muted-foreground py-8">
                        <GanttChartSquare className="mx-auto w-12 h-12 mb-4" />
                        <p>Nenhum andamento encontrado para este processo.</p>
                    </div>
                )}
            </CardContent>

            {isAddModalOpen && (
                <AddMovementModal
                    caseId={caseDetails.id}
                    currentDatajudJson={caseDetails.datajud_json as ProcessData}
                    isOpen={isAddModalOpen}
                    onOpenChange={setIsAddModalOpen}
                    onSuccess={handleAddMovementSuccess}
                />
            )}

            {isEditModalOpen && currentMovement && currentMovementIndex !== null && (
                <EditMovementModal
                    caseId={caseDetails.id}
                    currentMovement={currentMovement}
                    movementIndex={currentMovementIndex}
                    currentDatajudJson={caseDetails.datajud_json as ProcessData}
                    isOpen={isEditModalOpen}
                    onOpenChange={setIsEditModalOpen}
                    onSuccess={handleEditMovementSuccess}
                />
            )}
        </Card>
    );
};
